import { useEffect, useRef, useState } from 'react'
import type { TattooTimelineEntry } from '@/types/models'
import { TattooCard, AddTattooCard } from './TattooCard'
import { Loader } from 'lucide-react'

interface TattooTimelineProps {
  artistId: string
  clientId: string
  tattoos: TattooTimelineEntry[]
  isArtist?: boolean
  loading?: boolean
  onAddTattoo?: () => void
  onTattooClick?: (tattoo: TattooTimelineEntry) => void
  onAddHealed?: (tattoo: TattooTimelineEntry) => void
}

export function TattooTimeline({
  tattoos,
  isArtist = false,
  loading = false,
  onAddTattoo,
  onTattooClick,
  onAddHealed
}: TattooTimelineProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  // Sort tattoos by date (most recent first)
  const sortedTattoos = [...tattoos].sort((a, b) => 
    b.createdAt.getTime() - a.createdAt.getTime()
  )

  // Check scroll position
  const checkScroll = () => {
    if (!scrollContainerRef.current) return
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current
    setCanScrollLeft(scrollLeft > 0)
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10)
  }

  useEffect(() => {
    checkScroll()
    const container = scrollContainerRef.current
    if (container) {
      container.addEventListener('scroll', checkScroll)
      window.addEventListener('resize', checkScroll)
      return () => {
        container.removeEventListener('scroll', checkScroll)
        window.removeEventListener('resize', checkScroll)
      }
    }
  }, [tattoos])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (tattoos.length === 0 && !isArtist) {
    return null // Don't show empty state for clients
  }

  return (
    <div className="relative">
      {/* Section header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground">
          Your Tattoo Timeline
        </h2>
        {tattoos.length > 0 && (
          <span className="text-sm text-muted-foreground">
            {tattoos.length} {tattoos.length === 1 ? 'tattoo' : 'tattoos'}
          </span>
        )}
      </div>

      {/* Horizontal scrollable container */}
      <div className="relative">
        {/* Left fade gradient */}
        {canScrollLeft && (
          <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
        )}

        {/* Right fade gradient */}
        {canScrollRight && (
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
        )}

        {/* Scrollable content */}
        <div
          ref={scrollContainerRef}
          className="flex gap-6 pb-4 scrollbar-hide snap-x snap-mandatory flex-col"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
            display: 'flex',
            flexDirection: 'row',
            overflowX: 'auto'
          }}
        >
          {/* Tattoo cards */}
          {sortedTattoos.map((tattoo) => (
            <TattooCard
              key={tattoo.id}
              tattoo={tattoo}
              isArtist={isArtist}
              hideDate={true}
              onCardClick={() => onTattooClick?.(tattoo)}
              onAddHealed={() => onAddHealed?.(tattoo)}
            />
          ))}

          {/* Add tattoo card (only for artists) */}
          {isArtist && onAddTattoo && (
            <AddTattooCard onClick={onAddTattoo} hideDate={true} />
          )}
        </div>
      </div>

      {/* Empty state for artists */}
      {tattoos.length === 0 && isArtist && (
        <div className="text-center py-8">
          <p className="text-sm text-muted-foreground mb-4">
            Your tattoo journey together starts here
          </p>
          {onAddTattoo && (
            <button
              onClick={onAddTattoo}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
            >
              Add First Tattoo
            </button>
          )}
        </div>
      )}
    </div>
  )
}

